import { getEventBus, BridgeDataChanged } from '@databases/event-bus';
import { perf } from '@databases/logs/performance';
import { proxy } from 'valtio';

import { Deferrable } from '@canvas/deferrable';
import { type RawFrameToken } from '@canvas/frame-api-types';
import type { CanvasIdentity } from '@canvas/model/identity';

/**
 * Identity profile sent from CFE, it has a similar shape to {@link CanvasIdentity} but a
 * few differences:
 * * It currently sends the user's `displayName` but calls it `nickname`
 * * `type` is missing since it's application-specific context
 */
export type FrameIdentity = Omit<CanvasIdentity, 'type' | 'displayName'>;

export type BridgeData = {
  spaceKey: string;
  contentId: string;
  cloudId: string;
  activationId: string;
  contentReferenceId: string;
  userId: string;
  spaceId: string;
  spaceName: string;
  title: string;
  identity: FrameIdentity;
  token: RawFrameToken;
};

type BridgeDataState = {
  data: BridgeData;
};

export const currentBridgeData = proxy<BridgeDataState>({
  data: {
    spaceKey: '',
    contentId: '',
    cloudId: '',
    activationId: '',
    contentReferenceId: '',
    userId: '',
    spaceId: '',
    spaceName: '',
    title: '',
    identity: {
      accountId: '',
      nickname: '',
      picture: '',
      email: '',
      locale: '',
    },
    token: {
      container: '',
      board: '',
      accessToken: '',
      mediaToken: '',
      mediaBaseUrl: '',
      mediaCollectionId: '',
      policies: { dataSecurityPolicy: { pageExport: undefined } },
    },
  },
});

// frameContextReady resolves when setBridgeData() is first called
// await bridgeDataReady.get() if there's a chance that currentBridgeData may not be set yet
export const bridgeDataReady = new Deferrable<BridgeData>();

const setBridgeData = (rawBridgeData: string | null | undefined) => {
  if (!rawBridgeData) {
    return;
  }

  try {
    const parsedBridgeData = JSON.parse(rawBridgeData) as BridgeData;

    if (parsedBridgeData) {
      perf.timeLog('app-initialization', 'bridge-data');

      const eventBus = getEventBus();

      eventBus.dispatch(
        new BridgeDataChanged({
          oldContentId: currentBridgeData.data.contentId,
          newContentId: parsedBridgeData.contentId,
        })
      );

      currentBridgeData.data = parsedBridgeData;
      bridgeDataReady.resolve(currentBridgeData.data);
    }

    return parsedBridgeData;
  } catch (e) {
    throw new Error('Bridge data are not valid JSON');
  }
};

export const initializeBridgeData = async () => {
  if (window.location.search.includes('bridgeData')) {
    const params = new URLSearchParams(window.location.search);
    const rawData = params.get('bridgeData') as string;

    setBridgeData(rawData);

    return JSON.parse(rawData);
  }

  setBridgeData(window.frameElement?.getAttribute('data-bridge-data'));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        const rawBridgeData = window.frameElement?.getAttribute('data-bridge-data');

        if (rawBridgeData) {
          setBridgeData(rawBridgeData);
        }
      }
    });
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  observer.observe(window.frameElement!, {
    attributes: true,
    attributeFilter: ['data-bridge-data'],
  });

  return await bridgeDataReady.get();
};
